// Výšky sekcí kiosku na výšku (spec kiosek na výšku, kap. 3.2). Počítá se z výšky KONTEJNERU
// wrapperu, ne z okna, aby náhled v rámečku odpovídal monitoru. Přirozené výšky obsahu adresy
// a sekce 5 dodává volající ze skrytého měřicího prvku (Infinity = ještě nezměřeno → strop).
// CSS flex-shrink se nepoužívá: zmenšuje poměrně, kdežto tady se ubírá postupně v pořadí.

export interface VstupVyskySekci {
    vyskaKontejneru: number;
    pocetVozu: number;
    maPosilyZpravy: boolean;
    /** false = mapa vypnutá nebo poplach bez GPS → placeholder 8 % */
    mapaViditelna: boolean;
    vyskaObsahuAdresy: number;
    vyskaObsahuPosilZprav: number;
}

export interface VyskySekciNaVysku {
    hlavicka: number;
    adresa: number;
    mapy: number;
    technika: number;
    posilaZpravy: number;
    semafor: number;
    jmena: number;
}

/** Nejmenší výška sekce jmen: řádek „+ N dalších" s odsazením sekce. */
export const MIN_VYSKA_JMEN_PX = 48;

/**
 * Nejmenší výška sekce POSILA + zprávy, ve které je z obou zpráv vidět aspoň jeden celý řádek:
 * výpočet z ZpravyOdAdmina dává ~87 px, ale E2E (1024×1280, 6 vozů) při 96 px naměřil přetečení
 * POSILY o 8 px, skutečně je potřeba ~104 px. 112 = naměřeno + 8 px rezerva. Prioritní POSILA
 * nesmí zmizet; hlídá to kiosek-na-vysku.smoke.spec.ts v jsdh-e2e.
 */
export const MIN_VYSKA_POSIL_PX = 112;

export const vyskySekciNaVysku = (v: VstupVyskySekci): VyskySekciNaVysku => {
    // Pojistka: neplatná výška kontejneru (NaN, Infinity, nula, záporná) → vše 0.
    if (!Number.isFinite(v.vyskaKontejneru) || v.vyskaKontejneru <= 0) {
        return { hlavicka: 0, adresa: 0, mapy: 0, technika: 0, posilaZpravy: 0, semafor: 0, jmena: 0 };
    }
    const H = Math.floor(v.vyskaKontejneru);
    const p = (podil: number) => Math.round(H * podil);
    const obsah = (x: number) => (Number.isFinite(x) ? Math.max(0, Math.ceil(x)) : Number.MAX_SAFE_INTEGER);

    // Krok 1: přidělení podle tabulky; sekce podle obsahu dostanou min(obsah, strop).
    const hlavicka = p(0.12);
    let adresa = Math.min(obsah(v.vyskaObsahuAdresy), p(0.13));
    let mapy = v.mapaViditelna ? p(0.28) : p(0.08);
    const vozu4aVic = v.pocetVozu >= 4;
    let technika = vozu4aVic ? Math.max(p(0.17), 340) : Math.max(p(0.10), 190);
    let posila = v.maPosilyZpravy ? Math.min(obsah(v.vyskaObsahuPosilZprav), p(0.12)) : 0;
    const semafor = Math.max(p(0.04), 64);
    const cilJmen = p(0.20);
    const zbytek = () => H - hlavicka - adresa - mapy - technika - posila - semafor;

    // Krok 2: když jménům chybí do 20 %, ubírá se postupně a jen do chvíle, kdy je mají.
    const uber = (aktualni: number, mez: number): number => {
        const chybi = cilJmen - zbytek();
        if (chybi <= 0 || aktualni <= mez) return aktualni;
        return aktualni - Math.min(chybi, aktualni - mez);
    };
    if (v.mapaViditelna) mapy = uber(mapy, p(0.20)); // placeholder 8 % se nezmenšuje
    // Spodní mez posily je 5 %, ale ne méně než MIN_VYSKA_POSIL_PX a nikdy víc, než změřil obsah.
    posila = uber(posila, Math.min(obsah(v.vyskaObsahuPosilZprav), Math.max(p(0.05), MIN_VYSKA_POSIL_PX)));
    adresa = uber(adresa, p(0.09));
    if (vozu4aVic) technika = uber(technika, Math.max(p(0.15), 280));
    // Vyšší mez posily (nižší monitory) jménům ubrala; schází-li pořád, dorovná to mapa ještě
    // z 20 % na 15 %: mapa je orientační, kdežto jména a POSILA jsou informace pro výjezd.
    if (v.mapaViditelna) mapy = uber(mapy, p(0.15));

    // Krok 3: jména dostanou zbytek, ale vždy aspoň řádek „+ N dalších" – jinak se ubere z map.
    const chybiNaDalsi = MIN_VYSKA_JMEN_PX - zbytek();
    if (chybiNaDalsi > 0) mapy = Math.max(0, mapy - chybiNaDalsi);
    // Degradace (velmi nízký kontejner): ani s mapou na nule se řádek jmen nevejde → posila smí
    // zpět na starou mez 5 %, aby součet sekcí nepřesáhl výšku kontejneru.
    const dalsiDeficit = MIN_VYSKA_JMEN_PX - zbytek();
    if (dalsiDeficit > 0 && posila > p(0.05)) posila = Math.max(p(0.05), posila - dalsiDeficit);

    // Plné minimum MIN_VYSKA_JMEN_PX platí od ~530 px (6 vozů), resp. ~408 px (1 vůz). Pod tím
    // jde o degradaci: pevné spodní meze (technika 280/190 px, semafor 64 px) se s mapou na nule
    // nevejdou, jména dostanou méně než minimum (v pásmu ~465-530 px 1-47 px), pod ~450 px 0.
    // Výšky jsou vždy nezáporné a celé.
    return { hlavicka, adresa, mapy, technika, posilaZpravy: posila, semafor, jmena: Math.max(0, zbytek()) };
};
